import React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXT_INPUT_STYLES,
} from "../styles"

interface AbruptPitchChangesConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const PRESET_OPTIONS = [
  {
    key: "strict",
    label: "Strict",
    description: "Detects smaller pitch jumps",
  },
  {
    key: "normal",
    label: "Normal",
    description: "Catches noticeable pitch jumps",
  },
  {
    key: "lenient",
    label: "Lenient",
    description: "Only flags very obvious pitch jumps",
  },
  {
    key: "custom",
    label: "Custom",
    description: "Define your own thresholds",
  },
]

export const AbruptPitchChangesConfig: React.FC<
  AbruptPitchChangesConfigProps
> = ({ metricRecordData, onChange }) => {
  const hasLegacyFields =
    !metricRecordData.metric_metadata?.threshold_preset &&
    metricRecordData.metric_metadata &&
    (metricRecordData.metric_metadata.significant_changes_threshold_hz !==
      undefined ||
      metricRecordData.metric_metadata.min_change_for_variation_hz !==
        undefined)
  const currentPreset = hasLegacyFields
    ? "custom"
    : (metricRecordData.metric_metadata?.threshold_preset ?? "normal")
  const isCustom = currentPreset === "custom"

  const handlePresetChange = (value: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        threshold_preset: value as "strict" | "normal" | "lenient" | "custom",
      },
    })
  }

  const handleSignificantChangesThresholdHzChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        significant_changes_threshold_hz: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleMinChangeForVariationHzChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        min_change_for_variation_hz: isNaN(value) ? undefined : value,
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <span className={LABEL_STYLES}>Threshold Preset</span>
        </div>
        <Select value={currentPreset} onValueChange={handlePresetChange}>
          <SelectTrigger aria-label="Threshold Preset">
            <SelectValue placeholder="Select preset" />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                description={option.description}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          Select a preset or choose Custom to define your own thresholds.
        </p>
      </div>

      {isCustom && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={FIELD_CONTAINER_STYLES}>
            <div className={`${LABEL_ROW_STYLES} min-h-10`}>
              <span className={LABEL_STYLES}>
                Significant Changes Threshold (Hz)
              </span>
              <Tooltip
                content="Minimum pitch change in Hz to be considered significant."
                side="top"
              >
                <button type="button" className="inline-flex" aria-label="Info">
                  <Info className={INFO_ICON_STYLES} />
                </button>
              </Tooltip>
            </div>
            <input
              type="number"
              step="0.1"
              min="0"
              className={TEXT_INPUT_STYLES}
              value={
                metricRecordData.metric_metadata
                  ?.significant_changes_threshold_hz ?? ""
              }
              onChange={handleSignificantChangesThresholdHzChange}
              placeholder="2"
              aria-label="Significant Changes Threshold (Hz)"
            />
          </div>

          <div className={FIELD_CONTAINER_STYLES}>
            <div className={`${LABEL_ROW_STYLES} min-h-10`}>
              <span className={LABEL_STYLES}>
                Min Change for Variation (Hz)
              </span>
              <Tooltip
                content="Minimum pitch change in Hz to count as variation."
                side="top"
              >
                <button type="button" className="inline-flex" aria-label="Info">
                  <Info className={INFO_ICON_STYLES} />
                </button>
              </Tooltip>
            </div>
            <input
              type="number"
              step="0.1"
              min="0"
              className={TEXT_INPUT_STYLES}
              value={
                metricRecordData.metric_metadata?.min_change_for_variation_hz ??
                ""
              }
              onChange={handleMinChangeForVariationHzChange}
              placeholder="1"
              aria-label="Min Change for Variation (Hz)"
            />
          </div>
        </div>
      )}
    </div>
  )
}
